'use client';

import React, { useEffect, useState, useRef } from 'react';
import SwiperCore from 'swiper';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './blog_carousel.css';
import '@/components/hero/hero_carousel.css';
import dynamic from 'next/dynamic';
import { cn } from '@/lib/utils';
import { HeroBannerSlider } from '@/types/homepage/homepageTypes';
const BlogCard = dynamic(() => import('@/components/blogs/blog_card'));

SwiperCore.use([Pagination, Autoplay, Navigation]);

const Blogs = ({
  blogData,
  blogpage,
}: {
  blogData: HeroBannerSlider[];
  blogpage: boolean;
}) => {
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const swiperRef = useRef<any>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(false);

  const toggleAutoplay = () => {
    if (swiperRef.current) {
      const swiper = swiperRef?.current?.swiper;

      if (swiper?.autoplay?.running) {
        swiper?.autoplay?.stop();
        setAutoplayPaused(true);
      } else {
        swiper?.autoplay?.start();
        setAutoplayPaused(false);
      }
    }
  };

  useEffect(() => {
    if (imageRef?.current) {
      imageRef?.current?.remove();
    }

    const customImageEl = document.createElement('img');
    customImageEl.alt = autoplayPaused
      ? 'Play button for slideshow'
      : 'Pause button for slideshow';
    customImageEl.title = autoplayPaused ? 'Play Slideshow' : 'Pause Slideshow';
    customImageEl.src = autoplayPaused ? '/icons/play.svg' : '/icons/pause.svg';
    customImageEl.className = 'w-[13px] h-[13px] cursor-pointer invert';

    customImageEl.addEventListener('click', () => toggleAutoplay());

    const paginationContainer = document.querySelector(
      '.swiper_container2 .swiper-pagination'
    );

    if (paginationContainer) {
      paginationContainer.appendChild(customImageEl);
      imageRef.current = customImageEl;
    }

    return () => {
      if (imageRef.current) {
        imageRef.current.remove();
      }
    };
  }, [autoplayPaused]);

  useEffect(() => {
    const swiperInstance = swiperRef.current?.swiper;
    if (!swiperInstance) return;

    // Handler for interactions that should pause autoplay
    const handleInteraction = () => {
      if (swiperInstance.autoplay.running) {
        swiperInstance.autoplay.stop();
        setAutoplayPaused(true);
      }
    };

    // Attach event listeners to navigation buttons
    const navigationButtons = document.querySelectorAll(
      '.swiper-button-next, .swiper-button-prev'
    );
    navigationButtons.forEach((button) =>
      button.addEventListener('click', handleInteraction)
    );

    // Attach event listeners to pagination bullets
    const paginationBullets = document.querySelectorAll(
      '.swiper-pagination-bullet'
    );
    paginationBullets.forEach((bullet) =>
      bullet.addEventListener('click', handleInteraction)
    );

    // Listen for the end of a drag (touchEnd event)
    swiperInstance.on('touchEnd', handleInteraction);

    return () => {
      // Clean up the event listeners
      navigationButtons.forEach((button) =>
        button.removeEventListener('click', handleInteraction)
      );
      paginationBullets.forEach((bullet) =>
        bullet.removeEventListener('click', handleInteraction)
      );
      swiperInstance.off('touchEnd', handleInteraction);

      if (imageRef.current) {
        imageRef.current.remove();
      }
    };
  }, []);

  useEffect(() => {
    // Update the isLargeScreen state based on window size
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 768); // Adjust the breakpoint as needed
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="max-w-desktop bg-black rounded-[10px] md:mx-auto  ">
      <Swiper
        ref={swiperRef}
        effect="slide"
        preventClicks={false}
        preventClicksPropagation={false}
        noSwipingSelector="a"
        pagination={{
          clickable: true,
        }}
        loop={true}
        navigation={isLargeScreen || (blogpage && !isLargeScreen)} // Navigation on large screens or if blogpage is true on small screens
        autoplay={{
          delay: 8000,
          disableOnInteraction: false,
          pauseOnMouseEnter: false,
          waitForTransition: true,
        }}
        className={cn(
          'swiper_container2 rounded-[10px] bg-black overflow-hidden',
          autoplayPaused ? 'paused' : ''
        )}
      >
        {blogData?.map((props) => (
          <SwiperSlide key={props?.hero_banner_id?.id}>
            <BlogCard {...props.hero_banner_id} isBlogPage={blogpage} />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default Blogs;
